import { billingAuthorizationApi } from '@/lib/api/billing-authorizations.api'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

/**
 * Prefix for every client-authorization query. Invoice changes (create,
 * update, send, payment, void, delete) move authorization consumption, so
 * invoice mutations and the invoice:updated socket event invalidate it too.
 */
export const BILLING_AUTHORIZATIONS_QUERY_KEY = [
  'billing-authorizations',
] as const

export function useBillingAuthorizationsQuery(
  params: {
    entityId?: string
    projectId?: string
    contractId?: string
    status?: string | string[]
    search?: string
    page?: number
    limit?: number
  },
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['billing-authorizations', params],
    queryFn: () => billingAuthorizationApi.listAuthorizations(params),
    enabled: options.enabled ?? !!params.entityId,
  })
}

export function useBillingAuthorizationSourcesQuery(
  id: string,
  params: { page?: number; limit?: number; type?: 'time' | 'expense' },
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['billing-authorizations', id, 'sources', params],
    queryFn: () => billingAuthorizationApi.getSources(id, params),
    enabled: (options.enabled ?? true) && !!id,
    placeholderData: keepPreviousData,
  })
}

export function useEligibleBillingAuthorizationsQuery(
  params: {
    entityId?: string
    projectId?: string | null
    contractId?: string | null
    invoiceDate?: string
  },
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['billing-authorizations', 'eligible', params],
    queryFn: () =>
      billingAuthorizationApi.getEligible({
        entityId: params.entityId ?? '',
        projectId: params.projectId,
        contractId: params.contractId,
        invoiceDate: params.invoiceDate,
      }),
    enabled: (options.enabled ?? true) && !!params.entityId,
  })
}

/**
 * Org-wide Client Authorizations list (SD-123), shaped for useModuleTable:
 * store filters in, `{ data, meta }` out. Rows carry display names, owner
 * and forecast from the API.
 */
export function useBillingAuthorizationsTableQuery(
  filters: Record<string, unknown>,
  options: { enabled?: boolean } = {},
) {
  const status = filters.status as string[] | undefined
  const entityIds = filters.entityIds as string[] | undefined
  const params = {
    search: (filters.search as string | undefined) || undefined,
    status: status?.length ? status : undefined,
    entityId: entityIds?.length ? entityIds : undefined,
    page: Number(filters.page) || 1,
    limit: Number(filters.limit) || 50,
  }
  return useQuery({
    queryKey: [...BILLING_AUTHORIZATIONS_QUERY_KEY, 'org-list', params],
    queryFn: () => billingAuthorizationApi.listAuthorizations(params),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
  })
}

/**
 * useBillingAuthorizationsTableQuery under a non-hook name for the
 * useModuleTable config: passing a use*-named binding as a config VALUE makes
 * React Compiler bail out of the consuming component.
 */
export const billingAuthorizationsTableQuery =
  useBillingAuthorizationsTableQuery
